import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { Office365EmailActivity, IOffice365EmailActivity, Office365EmailLinkedService, ReferenceAdapter, ModelConstants } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Office365EmailActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            to: '*',
            subject: '*',
            body: '*',
            from: '*',
            cc: '*',
            bcc: '*',
            sensitivity: '*',
            replyTo: '*',
            importance: '*',
            upn: '*'
        },
        externalReferences: '*'
    }
})
export class Office365EmailActivityAdapter extends ActivityAsyncAdapter<Office365EmailActivity, IOffice365EmailActivity> {

    protected async _toResource(activity: Office365EmailActivity): Promise<IOffice365EmailActivity> {
        const modelOffice365EmailActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(Office365EmailActivity).name) as IOffice365EmailActivity;

        modelOffice365EmailActivity.typeProperties = {
            to: ValueAdapter.toResource(activity, Office365EmailActivity.TO),
            subject: ValueAdapter.toResource(activity, Office365EmailActivity.SUBJECT),
            body: Office365EmailActivity.BODY.templateValue(activity)
        };

        if (!stringIsNullOrEmpty(activity.from?.trim()) || Office365EmailActivity.FROM.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.from = ValueAdapter.toResource(activity, Office365EmailActivity.FROM);
        }
        if (!stringIsNullOrEmpty(activity.cc?.trim()) || Office365EmailActivity.CC.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.cc = ValueAdapter.toResource(activity, Office365EmailActivity.CC);
        }
        if (!stringIsNullOrEmpty(activity.bcc?.trim()) || Office365EmailActivity.BCC.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.bcc = ValueAdapter.toResource(activity, Office365EmailActivity.BCC);
        }
        if (!stringIsNullOrEmpty(activity.sensitivity?.trim()) || Office365EmailActivity.SENSITIVITY.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.sensitivity = ValueAdapter.toResource(activity, Office365EmailActivity.SENSITIVITY);
        }
        if (!stringIsNullOrEmpty(activity.replyTo?.trim()) || Office365EmailActivity.REPLY_TO.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.replyTo = ValueAdapter.toResource(activity, Office365EmailActivity.REPLY_TO);
        }
        if (!stringIsNullOrEmpty(activity.importance?.trim()) || Office365EmailActivity.IMPORTANCE.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.importance = ValueAdapter.toResource(activity, Office365EmailActivity.IMPORTANCE);
        }
        if (!stringIsNullOrEmpty(activity.upn?.trim()) || Office365EmailActivity.UPN.templateValue(activity) != undefined) {
            modelOffice365EmailActivity.typeProperties.upn = ValueAdapter.toResource(activity, Office365EmailActivity.UPN);
        }

        modelOffice365EmailActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.office365EmailConnection,
            activity.linkedServiceParameters);

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelOffice365EmailActivity, parsed);
        }

        return modelOffice365EmailActivity;
    }

    protected async _toModel(modelActivity: IOffice365EmailActivity): Promise<Office365EmailActivity> {
        const activity: Office365EmailActivity = Office365EmailActivity.MODEL.create<Office365EmailActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        ValueAdapter.toModel(activity, Office365EmailActivity.TO, modelActivity.typeProperties.to);
        ValueAdapter.toModel(activity, Office365EmailActivity.SUBJECT, modelActivity.typeProperties.subject);
        Office365EmailActivity.BODY.setTemplateValue(activity, modelActivity.typeProperties.body);

        ValueAdapter.toModel(activity, Office365EmailActivity.FROM, modelActivity.typeProperties.from);
        ValueAdapter.toModel(activity, Office365EmailActivity.CC, modelActivity.typeProperties.cc);
        ValueAdapter.toModel(activity, Office365EmailActivity.BCC, modelActivity.typeProperties.bcc);
        ValueAdapter.toModel(activity, Office365EmailActivity.SENSITIVITY, modelActivity.typeProperties.sensitivity);
        ValueAdapter.toModel(activity, Office365EmailActivity.REPLY_TO, modelActivity.typeProperties.replyTo);
        ValueAdapter.toModel(activity, Office365EmailActivity.IMPORTANCE, modelActivity.typeProperties.importance);
        ValueAdapter.toModel(activity, Office365EmailActivity.UPN, modelActivity.typeProperties.upn);

        if(modelActivity.linkedServiceName !== undefined){
            activity.office365EmailConnection = getFabricLsForParameterizedConnections(
                            modelActivity.linkedServiceName,
                            ModelConstants.office365EmailLinkedService, 
                            activity);
            if(activity.office365EmailConnection === undefined) {                       
                activity.office365EmailConnection = ReferenceAdapter.toProxyModel<Office365EmailLinkedService>(Metamodel.modelFor(Office365EmailLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, Office365EmailActivity.MODEL));
        return activity;
    }
}
